import clsx from "clsx"
import { useMemo } from "react"

import useDebouncedCallback from "@hooks/useDebouncedCallback"

import { faIcon } from "@utils/faIcon"

import { ExtraTouchArea, Skeleton } from "@particles"

import { Icon, Tooltip } from "@atoms"
import { IconSize } from "@atoms/Icon/Icon.types"

import useTabStack from "@molecules/TabStack/TabStack.store"
import { TabStackProps } from "@molecules/TabStack/TabStack.types"

import styles from "./TabItem.module.scss"
import { TabItemProps } from "./TabItem.types"

export default function TabItem(props: TabItemProps) {
    const {
        icon,
        iconActive,
        iconInReactNode,
        children,
        count,
        noDebounce,
        title,
        orientation = "horizontal",

        onClick,

        isLoading = false,
        isPressed = false,
        isDisabled = false,
        isTabbable = true,
        isSelected = false,
        hasWarning = false,
        _isFocused = false,
        _isHovered = false,
    } = props

    const debouncedClick = useDebouncedCallback(onClick)

    const { variant, size } = useTabStack()

    const iconSize = useMemo(() => {
        const sizeMap: { [key in TabStackProps["size"]]: IconSize } = {
            sm: 12,
            md: 14,
            lg: 14,
        }
        return sizeMap[size]
    }, [size])

    const desiredIcon = useMemo(() => {
        if (iconInReactNode) {
            return iconInReactNode
        } else if (iconActive && isSelected) {
            return <Icon icon={iconActive} size={iconSize} container="square" />
        } else if (icon) {
            return <Icon icon={icon} size={iconSize} container="square" />
        } else {
            return null
        }
    }, [icon, iconActive, iconInReactNode, isSelected, iconSize])

    const isIconOnly = useMemo(() => {
        return (icon || iconActive || iconInReactNode) && !children && count === undefined
    }, [icon, iconInReactNode, children, count])

    const skeletonWidth = useMemo(() => (size === "sm" ? 40 : 48), [size])

    return (
        <Tooltip body={title} isDisabled={!title}>
            <ExtraTouchArea extraTouchArea={variant === "underlined" ? 10 : 0} sides="horizontal">
                <button
                    onClick={noDebounce ? onClick : debouncedClick}
                    type="button"
                    disabled={isDisabled || isLoading}
                    role="tab"
                    aria-selected={isSelected}
                    className={clsx(styles.base, styles[`${size}Size`], {
                        [styles.verticalOrientation]: orientation === "vertical",
                        [styles.isLoading]: isLoading,
                        [styles.isPressed]: isPressed,
                        [styles.isSelected]: isSelected,
                        [styles.isFocused]: _isFocused,
                        [styles.isHovered]: _isHovered,
                        [styles.contained]: variant === "contained",
                        [styles.underlined]: variant === "underlined",
                        [styles.iconOnly]: isIconOnly,
                        [styles.hasWarning]: hasWarning,
                    })}
                    tabIndex={isTabbable ? 0 : -1}
                >
                    {isLoading ? (
                        isIconOnly ? (
                            <Skeleton circle={true} width={12} height={12} />
                        ) : (
                            <Skeleton borderRadius={0} width={skeletonWidth} />
                        )
                    ) : (
                        <>
                            {hasWarning && (
                                <span className={styles.warningIcon}>
                                    <Icon
                                        icon={faIcon({ name: "triangle-exclamation", style: "solid" })}
                                        size={iconSize}
                                        container="square"
                                    />
                                </span>
                            )}
                            {(icon || iconInReactNode) && desiredIcon}
                            {children && <div className={styles.content}>{children}</div>}
                            {count !== undefined && <div>{count}</div>}
                        </>
                    )}
                </button>
            </ExtraTouchArea>
        </Tooltip>
    )
}
